import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Target, ArrowRight, Info 
} from 'lucide-react';
import GradientText from '../components/GradientText';

const BenchmarkingCalculator = () => {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState({
    // Company Basics
    industry: '',
    companySize: '',
    // CSE
    ticketsPerMonth: '',
    responseTimeHrs: '',
    autoHandlingPct: '0%',
    // OE + DMS
    manualHoursPerWeek: '',
    reportDays: '',
    // IGM
    contentPerMonth: '',
    timeToLaunchMonths: ''
  });

  const [results, setResults] = useState(null);
  const [lead, setLead] = useState({ name: '', email: '', company: '', role: '', wantsConsult: false });

  const industries = ['Healthcare', 'Finance', 'Retail/eCommerce', 'Legal', 'Manufacturing', 'Other'];
  const companySizes = ['Small (<100)', 'Mid (100–1000)', 'Enterprise (>1000)'];

  const handle = (field) => (e) => setInputs((p) => ({ ...p, [field]: e.target.value }));
  const next = () => setStep((s) => Math.min(s + 1, 5));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const compute = () => {
    // Normalize user metrics to 0-100 scales against simple AI peer targets
    const peer = {
      responseTimeHrs: 3.5, // peers ~3.5 hrs
      manualHoursPerWeek: 12, // peers ~12 hrs
      reportDays: 2, // peers ~2 days
      contentMultiplier: 2.0, // peers ~2x
    };

    const cse = Math.max(0, Math.min(100, 100 - ((Number(inputs.responseTimeHrs) || 0) / peer.responseTimeHrs) * 50 + (inputs.autoHandlingPct === '>50%' ? 30 : inputs.autoHandlingPct === '25–50%' ? 20 : inputs.autoHandlingPct === '<25%' ? 10 : 0)));
    const oe = Math.max(0, Math.min(100, 100 - ((Number(inputs.manualHoursPerWeek) || 0) / peer.manualHoursPerWeek) * 50));
    const dms = Math.max(0, Math.min(100, 100 - ((Number(inputs.reportDays) || 0) / peer.reportDays) * 50));
    const igm = Math.max(0, Math.min(100, Math.min(100, ((Number(inputs.contentPerMonth) || 0) > 0 ? 60 : 40) + (inputs.timeToLaunchMonths ? Math.max(0, 40 - Number(inputs.timeToLaunchMonths) * 5) : 0))));

    const yourScore = Math.round((cse + oe + dms + igm) / 4);
    const peerAvg = 75;
    const summary = {
      yourScore,
      peerAvg,
      savingsCopy: 'With AI, you could reduce costs by ~30%, save ~25 hrs/week, and speed decision-making by 60%.',
      kpis: {
        responseTime: { you: Number(inputs.responseTimeHrs) || 0, peers: 3.5 },
        manualHours: { you: Number(inputs.manualHoursPerWeek) || 0, peers: 12 },
        reportingTime: { you: Number(inputs.reportDays) || 0, peers: 2 },
        launchCycle: { you: Number(inputs.timeToLaunchMonths) || 0, peers: 5 }
      },
      radar: { cse: Math.round(cse), oe: Math.round(oe), dms: Math.round(dms), igm: Math.round(igm) }
    };

    setResults(summary);
    setStep(5);
  };

  const generateRecommendations = (gap, inputs) => {
    const recommendations = [];
    
    if (gap > 30) {
      recommendations.push({
        priority: 'High',
        title: 'AI Strategy Development',
        description: 'Develop a comprehensive AI strategy and roadmap',
        impact: 'High'
      });
    }
    
    if (inputs.dataQuality < 60) {
      recommendations.push({
        priority: 'High',
        title: 'Data Quality Improvement',
        description: 'Invest in data cleaning and governance',
        impact: 'High'
      });
    }
    
    if (inputs.automationLevel < 50) {
      recommendations.push({
        priority: 'Medium',
        title: 'Process Automation',
        description: 'Implement workflow automation tools',
        impact: 'Medium'
      });
    }
    
    if (inputs.techMaturity < 60) {
      recommendations.push({
        priority: 'Medium',
        title: 'Technology Infrastructure',
        description: 'Upgrade technology stack and cloud infrastructure',
        impact: 'Medium'
      });
    }

    return recommendations;
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            y: [-20, 20, -20],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-10 w-4 h-4 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full opacity-30 blur-sm"
        />
        <motion.div
          animate={{
            y: [20, -20, 20],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-40 right-20 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 blur-sm"
        />
      </div>

      <div className="relative z-10 py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Intro / Landing */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-full border border-purple-400/30 mb-8"
            >
              <BarChart3 className="w-6 h-6 text-purple-300" />
              <span className="font-semibold text-purple-200">Industry Benchmarking</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-6xl md:text-7xl font-black mb-8 leading-tight"
            >
              AI <GradientText>Benchmarking</GradientText> Calculator
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-2xl text-slate-200 max-w-4xl mx-auto mb-12 leading-relaxed"
            >
              See how much efficiency, savings, and growth you may be missing without AI.
            </motion.p>
          </motion.div>

          {/* Wizard Card */}
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10 max-w-4xl mx-auto">
            <div className="flex items-center justify-between text-sm text-slate-300 mb-6">
              <div>Step {step + 1} of 6</div>
              <div className="h-2 bg-white/10 rounded w-40">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded" style={{ width: `${((step + 1) / 6) * 100}%` }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div key="intro" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                  <h2 className="text-2xl font-bold mb-2">Find Out How You Compare</h2>
                  <p className="text-slate-300 mb-6">See how your company stacks up against AI-enabled peers across Support, Operations, Decisions, and Innovation.</p>
                  <button onClick={next} className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold flex items-center gap-2">
                    Start Benchmark <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div key="company" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                  <h2 className="text-2xl font-bold mb-4">Company Basics</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-2">Industry</label>
                      <select value={inputs.industry} onChange={handle('industry')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg">
                        <option value="">Select</option>
                        {industries.map((i) => (<option key={i} value={i}>{i}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-2">Company size</label>
                      <select value={inputs.companySize} onChange={handle('companySize')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg">
                        <option value="">Select</option>
                        {companySizes.map((i) => (<option key={i} value={i}>{i}</option>))}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-slate-400 mt-2"><Info className="w-3 h-3 mr-1" /> Benchmarks are tailored by industry and size.</div>
                  <div className="flex gap-3 mt-6"><button onClick={back} className="px-4 py-2 border border-white/20 rounded">Back</button><button onClick={next} className="px-6 py-2 bg-purple-600 rounded">Next</button></div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="cse" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                  <h2 className="text-2xl font-bold mb-4">Customer Support Metrics</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm mb-2">Tickets per month</label>
                      <input type="number" value={inputs.ticketsPerMonth} onChange={handle('ticketsPerMonth')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 2000" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">Avg response time (hrs)</label>
                      <input type="number" value={inputs.responseTimeHrs} onChange={handle('responseTimeHrs')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 6" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">% tickets automated</label>
                      <select value={inputs.autoHandlingPct} onChange={handle('autoHandlingPct')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg">
                        {['0%', '<25%', '25–50%', '>50%'].map((i) => (<option key={i} value={i}>{i}</option>))}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-slate-400 mt-2"><Info className="w-3 h-3 mr-1" /> AI-enabled peers resolve tickets 30–40% faster.</div>
                  <div className="flex gap-3 mt-6"><button onClick={back} className="px-4 py-2 border border-white/20 rounded">Back</button><button onClick={next} className="px-6 py-2 bg-purple-600 rounded">Next</button></div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="oe" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                  <h2 className="text-2xl font-bold mb-4">Operations & Decisions</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-2">Hours/week on manual processes</label>
                      <input type="number" value={inputs.manualHoursPerWeek} onChange={handle('manualHoursPerWeek')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 25" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">Avg time to create reports (days)</label>
                      <input type="number" value={inputs.reportDays} onChange={handle('reportDays')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 5" />
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-slate-400 mt-2"><Info className="w-3 h-3 mr-1" /> AI automation can save up to 20–40% of manual workload.</div>
                  <div className="flex gap-3 mt-6"><button onClick={back} className="px-4 py-2 border border-white/20 rounded">Back</button><button onClick={next} className="px-6 py-2 bg-purple-600 rounded">Next</button></div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div key="igm" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                  <h2 className="text-2xl font-bold mb-4">Innovation & Go-to-Market</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-2">Content per month</label>
                      <input type="number" value={inputs.contentPerMonth} onChange={handle('contentPerMonth')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 40" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">Time to launch new feature (months)</label>
                      <input type="number" value={inputs.timeToLaunchMonths} onChange={handle('timeToLaunchMonths')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 8" />
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-slate-400 mt-2"><Info className="w-3 h-3 mr-1" /> Generative AI can double content output and cut time-to-market by 25–40%.</div>
                  <div className="flex gap-3 mt-6"><button onClick={back} className="px-4 py-2 border border-white/20 rounded">Back</button><button onClick={compute} className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded">See My Results</button></div>
                </motion.div>
              )}

              {step === 5 && results && (
                <motion.div key="results" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                  <h2 className="text-2xl font-bold mb-2">Your AI Benchmark Results</h2>
                  <p className="text-slate-300 mb-6">Here’s how you compare against AI-enabled peers.</p>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4" /><span className="font-semibold">Radar (placeholder)</span></div>
                      <div className="text-slate-300 text-sm">CSE: {results.radar.cse}, OE: {results.radar.oe}, DMS: {results.radar.dms}, IGM: {results.radar.igm}</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4" /><span className="font-semibold">KPIs vs Peers</span></div>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div className="flex justify-between"><span>Response Time (hrs)</span><span>You: {results.kpis.responseTime.you} vs Peers: {results.kpis.responseTime.peers}</span></div>
                        <div className="flex justify-between"><span>Manual Hours (wk)</span><span>You: {results.kpis.manualHours.you} vs Peers: {results.kpis.manualHours.peers}</span></div>
                        <div className="flex justify-between"><span>Reporting Time (days)</span><span>You: {results.kpis.reportingTime.you} vs Peers: {results.kpis.reportingTime.peers}</span></div>
                        <div className="flex justify-between"><span>Launch Cycle (months)</span><span>You: {results.kpis.launchCycle.you} vs Peers: {results.kpis.launchCycle.peers}</span></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
                    <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4" /><span className="font-semibold">Summary</span></div>
                    <div className="text-slate-300 text-sm">
                      Your AI Benchmark Score: <span className="text-white font-semibold">{results.yourScore}/100</span> (Peers avg: {results.peerAvg}/100). {results.savingsCopy}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <h3 className="font-semibold mb-2">📑 Get Your Full Benchmark Report</h3>
                      <div className="grid md:grid-cols-4 gap-3">
                        <input placeholder="Name" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.name} onChange={(e)=>setLead({...lead,name:e.target.value})} />
                        <input placeholder="Email" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.email} onChange={(e)=>setLead({...lead,email:e.target.value})} />
                        <input placeholder="Company" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.company} onChange={(e)=>setLead({...lead,company:e.target.value})} />
                        <input placeholder="Role" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.role} onChange={(e)=>setLead({...lead,role:e.target.value})} />
                      </div>
                      <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={lead.wantsConsult} onChange={(e)=>setLead({...lead,wantsConsult:e.target.checked})} /> Yes, I’d like a free consultation.</label>
                      <button className="mt-3 px-5 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded">Send My Report</button>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <h3 className="font-semibold mb-2">Next Steps</h3>
                      <div className="text-sm text-slate-300">Prefer a conversation? <a href="/get-started" className="text-white underline">Book Free AI Consultation</a></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenchmarkingCalculator;
